import * as React from "react";
import { motion, AnimatePresence, useReducedMotion } from "motion/react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

export function BottomSheet({
  open,
  onClose,
  title,
  children,
  className,
}: {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  className?: string;
}) {
  // Motion анимирует инлайн-трансформом, CSS-медиазапрос его не остановит — нужен хук.
  const reduceMotion = useReducedMotion();

  React.useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Шторка едет строкой transform, а не `y`: так анимацию ведёт браузер (WAAPI) на
  // компоновщике, и она не дёргается, пока монтируется тяжёлое содержимое. Пружина на
  // `y` считалась в JS на главном потоке. Под затемнением нет размытия: blur во весь
  // экран пересчитывался каждый кадр открытия и на слабом телефоне ронял кадры.
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={reduceMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={reduceMotion ? { opacity: 0, transition: { duration: 0 } } : { opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-end bg-black/60"
          onClick={onClose}
        >
          <motion.div
            initial={reduceMotion ? false : { transform: 'translateY(100%)' }}
            animate={{ transform: 'translateY(0%)' }}
            exit={reduceMotion ? { transform: 'translateY(100%)', transition: { duration: 0 } } : { transform: 'translateY(100%)', transition: { duration: 0.2, ease: [0.32, 0.72, 0, 1] } }}
            transition={reduceMotion ? { duration: 0 } : { duration: 0.28, ease: [0.32, 0.72, 0, 1] }}
            className={cn(
              "w-full max-w-md mx-auto bg-card rounded-t-3xl px-6 pt-3 border-t border-white/10 shadow-[0_-8px_32px_rgba(0,0,0,0.4)] flex flex-col",
              className,
            )}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxHeight: '90dvh',
              paddingBottom: 'calc(env(safe-area-inset-bottom) + 1.5rem)',
            }}
          >
            <div className="w-10 h-1 rounded-full bg-white/15 mx-auto shrink-0" aria-hidden />
            {title && (
              <div className="flex items-center justify-between pt-3 pb-2 shrink-0">
                <h3 className="text-xl font-bold">{title}</h3>
                {/* Кружок остаётся 36px, хит-область растянута до 44px (WCAG 2.5.5). */}
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Закрыть"
                  className="relative w-9 h-9 rounded-full bg-white/8 hover:bg-white/15 flex items-center justify-center transition-colors after:absolute after:-inset-1 after:content-['']"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            {/* Скроллируемый контент — длинные формы (composer рассылки с эмодзи-реакциями
                и расписанием) уходили за экран, юзер не мог дотянуться до «Отправить». */}
            <div className="flex-1 overflow-y-auto overscroll-contain space-y-5 -mx-2 px-2 pb-2">
              {children}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'OK',
  cancelLabel = 'Отмена',
  destructive = false,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
}) {
  return (
    <BottomSheet open={open} onClose={onClose} title={title}>
      {description && <p className="text-sm text-muted-foreground">{description}</p>}
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={onClose}
          className="bg-muted text-foreground py-3 rounded-xl text-sm font-bold active:scale-[0.98] transition-transform"
        >
          {cancelLabel}
        </button>
        <button
          type="button"
          onClick={() => {
            onConfirm();
            onClose();
          }}
          className={cn(
            "py-3 rounded-xl text-sm font-bold active:scale-[0.98] transition-transform",
            destructive ? "bg-red-500 text-white" : "bg-primary text-primary-foreground"
          )}
        >
          {confirmLabel}
        </button>
      </div>
    </BottomSheet>
  );
}
